// Deterministic data model for the 3x3x3 magnetic cube (27 modules).
// Every module has a stable id, grid target, hand-composed scatter origin, curved path, cascade delay and idle phase.

export const GRID = 3;
export const MODULE_SIZE = 1;
export const STEP = 1.06;
export const COUNT = GRID * GRID * GRID;

export type ModuleKind = "field" | "deep" | "fore";
/** 0 graphite titanium · 1 brushed titanium · 2 polished metal · 3 smoked glass · 4 energy core */
export type Tone = 0 | 1 | 2 | 3 | 4;

export interface ModuleSpec {
  id: number;
  grid: [number, number, number];
  target: [number, number, number];
  kind: ModuleKind;
  /** screen fraction u, v + world depth z */
  origin: [number, number, number];
  rot: [number, number, number];
  swirl: number;
  lift: number;
  delay: number;
  phase: number;
  tone: Tone;
  /** 0..1 emission strength of the embedded seams */
  glow: number;
}

function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** hand-composed field: balanced left/right/top/bottom, deep units behind the copy, two foreground units at the frame */
const ORIGINS: [number, number, number][] = [
  // left flank
  [0.06, 0.15, -2], [0.17, 0.31, 1.4], [0.08, 0.5, 4.2], [0.2, 0.63, -1], [0.1, 0.83, 0.6], [0.24, 0.9, -3.6], [0.25, 0.1, -4.6],
  // right flank
  [0.94, 0.17, -1.5], [0.83, 0.29, 1], [0.92, 0.52, 4.4], [0.8, 0.64, -2.6], [0.9, 0.84, 1.2], [0.76, 0.92, -4.2], [0.75, 0.08, -3.2],
  // top band
  [0.37, 0.11, -1.8], [0.52, 0.06, -5.2], [0.65, 0.15, 0.2], [0.45, 0.21, -6.6],
  // bottom band
  [0.34, 0.87, 0.5], [0.5, 0.94, -2.2], [0.65, 0.85, -0.6], [0.42, 0.79, -6.2], [0.58, 0.77, -7],
  // deep, small, behind the headline
  [0.31, 0.42, -9], [0.69, 0.38, -8.6], [0.36, 0.64, -8.2], [0.66, 0.66, -9.4],
];

const ENERGY = new Set([13, 2, 24]);
const GLASS = new Set([5, 9, 19, 25]);
const POLISHED = new Set([7, 21]);

export function buildModules(): ModuleSpec[] {
  const r = mulberry32(20261026);
  const range = (a: number, b: number) => a + (b - a) * r();

  // stable mapping origin → grid slot: screen-left modules fill the left column, top fills top, near fills front
  const byU = ORIGINS.map((o, i) => ({ o, i })).sort((a, b) => a.o[0] - b.o[0]);
  const assign = new Map<number, [number, number, number]>();
  for (let gx = 0; gx < GRID; gx += 1) {
    const col = byU.slice(gx * 9, gx * 9 + 9).sort((a, b) => a.o[1] - b.o[1]);
    for (let row = 0; row < GRID; row += 1) {
      const gy = GRID - 1 - row;
      const slab = col.slice(row * 3, row * 3 + 3).sort((a, b) => a.o[2] - b.o[2]);
      slab.forEach((c, gz) => assign.set(gx + gy * GRID + gz * GRID * GRID, c.o));
    }
  }

  const half = (GRID - 1) / 2;
  const maxR = Math.hypot(half, half, half) * STEP;
  const list: ModuleSpec[] = [];
  for (let id = 0; id < COUNT; id += 1) {
    const gx = id % GRID;
    const gy = Math.floor(id / GRID) % GRID;
    const gz = Math.floor(id / (GRID * GRID));
    const target: [number, number, number] = [(gx - half) * STEP, (gy - half) * STEP, (gz - half) * STEP];
    const o = assign.get(id) as [number, number, number];
    const tRad = Math.hypot(...target) / maxR;
    const tone: Tone = ENERGY.has(id) ? 4 : GLASS.has(id) ? 3 : POLISHED.has(id) ? 2 : r() < 0.55 ? 0 : 1;
    list.push({
      id,
      grid: [gx, gy, gz],
      target,
      kind: o[2] < -7.5 ? "deep" : o[2] > 3.5 ? "fore" : "field",
      origin: o,
      rot: [range(-1.1, 1.1), range(-1.1, 1.1), range(-1.1, 1.1)],
      swirl: range(0.16, 0.3),
      lift: range(-1.6, 1.6),
      delay: Math.min(1, 0.62 * tRad + range(0, 0.38)),
      phase: r() * Math.PI * 2,
      tone,
      glow: tone === 4 ? 1 : tone === 3 ? range(0.45, 0.7) : range(0.12, 0.5),
    });
  }
  return list;
}